// 刪除帳號 (spec 20 §5, GDPR + Apple 5.1.1(v)) —
// preflight warnings → type-DELETE + password confirm → 202 → pending-deletion terminal screen.
// v1 限制（20 §5.4，必須如實陳述）：受理即撤全部 token，App/網頁內無法取消刪除；
// 冷靜期內唯一反悔路徑 = email 確認信中的取消連結 / 聯絡客服。
import { useCallback, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { ApiError, apiFetch } from '../../lib/api';
import { useAuth } from '../../auth/AuthContext';
import { formatTimestampUs, formatTimestampS } from '../../lib/format';
import { PasswordInput } from '../../components/PasswordInput';

interface Preflight {
  owned_devices: { device_id: string; display_name: string; shared_user_count: number }[];
  owned_device_count: number;
  shared_to_me_count: number;
  active_subscriptions: { device_id: string; status: string; expired_time?: number }[];
  affected_shared_users: { device_id: string; shared_user_count: number }[];
  warnings: string[];
  cooldown_days: number | string;
  billing_retention_note?: string;
}

interface DeletionAccepted {
  deletion_request_id: string;
  status: string;
  // 冷靜期截止:as-built 實際欄位 = `cooling_until_s`(epoch 秒,restful 02 §6.3);
  // `cooldown_ends_at`(μs)為舊名 fallback,避免契約再變時整頁顯「—」(對齊 mobile,spec 20 §5.3)。
  cooling_until_s?: number | string;
  cooldown_ends_at?: number | string;
  cancellable_until?: number | string;
}

const REASONS: { value: string; labelKey: string }[] = [
  { value: '', labelKey: 'account.reasonNone' },
  { value: 'no_longer_use', labelKey: 'account.reasonNoLongerUse' },
  { value: 'privacy', labelKey: 'account.reasonPrivacy' },
  { value: 'switching', labelKey: 'account.reasonSwitching' },
  { value: 'other', labelKey: 'account.reasonOther' },
];

export default function DeleteAccountCard() {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const [step, setStep] = useState<'entry' | 'confirm' | 'accepted'>('entry');
  const [preflight, setPreflight] = useState<Preflight | null>(null);
  const [accepted, setAccepted] = useState<DeletionAccepted | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [confirmText, setConfirmText] = useState('');
  const [password, setPassword] = useState('');
  const [reason, setReason] = useState('');

  const loadPreflight = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await apiFetch<Preflight>('/v3/me/deletion-preflight');
      // defense-in-depth: a malformed backend shape must degrade, not unmount the
      // SPA (e2e r1: owned_devices arrived as a number → .map() TypeError → white page)
      const raw = (res.data ?? {}) as Partial<Preflight> & Record<string, unknown>;
      const arr = <T,>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);
      setPreflight({
        ...raw,
        owned_devices: arr(raw.owned_devices),
        active_subscriptions: arr(raw.active_subscriptions),
        affected_shared_users: arr(raw.affected_shared_users),
        warnings: arr(raw.warnings),
        owned_device_count:
          typeof raw.owned_device_count === 'number' ? raw.owned_device_count : arr(raw.owned_devices).length,
        shared_to_me_count: typeof raw.shared_to_me_count === 'number' ? raw.shared_to_me_count : 0,
        cooldown_days: (raw.cooldown_days as number | string) ?? '—',
      });
      setStep('confirm');
    } catch (e) {
      if (e instanceof ApiError && e.code === 'ALREADY_DELETING') {
        setError(t('account.alreadyDeletingDot'));
      } else {
        setError(e instanceof ApiError ? `${e.message}（${e.code}）` : t('common.loadFailedRetry'));
      }
    } finally {
      setBusy(false);
    }
  }, [t]);

  const submitDelete = useCallback(async () => {
    setError(null);
    if (confirmText !== 'DELETE') {
      // client-side gate mirrors backend confirm rule (422 BUSINESS_RULE_VIOLATION should never fire)
      setError(t('account.deleteConfirmPrompt'));
      return;
    }
    if (!password) {
      setError(t('account.enterPassword'));
      return;
    }
    setBusy(true);
    try {
      const res = await apiFetch<DeletionAccepted>('/v3/me', {
        method: 'DELETE',
        body: { confirm: 'DELETE', password, ...(reason ? { reason } : {}) },
        idempotencyKey: crypto.randomUUID(),
      });
      setAccepted(res.data);
      setStep('accepted');
    } catch (e) {
      if (e instanceof ApiError) {
        const map: Record<string, string> = {
          // backend re-verifies the password server-side and returns 403 WRONG_PASSWORD
          // (deliberately not 401, so apiFetch's refresh-and-replay never fires on it)
          WRONG_PASSWORD: t('account.errWrongPassword'),
          ALREADY_DELETING: t('account.errAlreadyDeleting'),
          RATE_LIMITED: t('account.errRateLimited'),
          BUSINESS_RULE_VIOLATION: t('account.errConfirmMismatch'),
        };
        setError(map[e.code] ?? `${e.message}（${e.code}）`);
      } else {
        setError(t('common.submitFailedNetwork'));
      }
    } finally {
      setBusy(false);
    }
  }, [confirmText, password, reason, t]);

  // terminal screen (20 §5.3): show request id + cooldown, then complete local logout
  if (step === 'accepted' && accepted) {
    // 實際欄位 `cooling_until_s`(秒)優先,舊名 `cooldown_ends_at`(μs)fallback → 兩者皆缺才顯「—」。
    const coolingUntilText =
      accepted.cooling_until_s !== undefined && accepted.cooling_until_s !== null && accepted.cooling_until_s !== ''
        ? formatTimestampS(accepted.cooling_until_s)
        : accepted.cooldown_ends_at !== undefined && accepted.cooldown_ends_at !== null && accepted.cooldown_ends_at !== ''
          ? formatTimestampUs(accepted.cooldown_ends_at)
          : '—';
    return (
      <div className="card" style={{ borderColor: 'var(--color-semantic-danger)' }}>
        <h2 style={{ marginTop: 0, fontSize: 'var(--font-size-xl)' }}>{t('account.deleteAccepted')}</h2>
        <p>
          {t('account.deleteRequestId')}<code style={{ fontFamily: 'var(--font-family-mono)' }}>{accepted.deletion_request_id}</code>
        </p>
        <p>
          {t('account.cooldownEndsAt', { date: coolingUntilText })}
        </p>
        <p>{t('account.confirmEmailSent')}</p>
        <p className="error-text">
          {t('account.deleteAcceptedNotice')}
        </p>
        <button className="btn-primary" onClick={() => void logout()}>
          {t('account.doneGoToLogin')}
        </button>
      </div>
    );
  }

  return (
    <div className="card">
      <h2 style={{ marginTop: 0, fontSize: 'var(--font-size-xl)' }}>{t('account.deleteAccount')}</h2>

      {step === 'entry' && (
        <div>
          <p className="text-secondary" style={{ marginTop: 0 }}>
            {t('account.deleteEntryDesc')}
          </p>
          {error && <p className="error-text">{error}</p>}
          <button className="btn-danger" disabled={busy} onClick={() => void loadPreflight()}>
            {busy ? t('common.loading') : t('account.deleteAccountCta')}
          </button>
        </div>
      )}

      {step === 'confirm' && preflight && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-5)' }}>
          <h3 style={{ margin: 0, fontSize: 'var(--font-size-lg)' }}>{t('account.deleteConsequences')}</h3>
          <ul style={{ margin: 0, paddingLeft: 'var(--spacing-10)' }}>
            <li>
              {t('account.consequenceDevices', { count: preflight.owned_device_count })}
              {preflight.owned_devices.length > 0 && (
                <span className="text-secondary">
                  {t('account.consequenceDeviceNames', { names: preflight.owned_devices.map((d) => d.display_name).join('、') })}
                </span>
              )}
            </li>
            {preflight.active_subscriptions.length > 0 && (
              <li>
                <Trans
                  i18nKey="account.consequenceSubscriptions"
                  count={preflight.active_subscriptions.length}
                  values={{ count: preflight.active_subscriptions.length }}
                  components={{ strong: <strong /> }}
                />
              </li>
            )}
            {preflight.affected_shared_users.length > 0 && (
              <li>
                {t('account.consequenceSharedUsers', { count: preflight.affected_shared_users.reduce((n, a) => n + a.shared_user_count, 0) })}
              </li>
            )}
            {preflight.shared_to_me_count > 0 && (
              <li>{t('account.consequenceSharedToMe', { count: preflight.shared_to_me_count })}</li>
            )}
            <li>
              {t('account.consequenceCooldownPrefix', { days: typeof preflight.cooldown_days === 'number' ? t('account.daysValue', { count: preflight.cooldown_days }) : '—' })}
              <strong>{t('account.consequenceCancelOnly')}</strong>
            </li>
            <li>{t('account.consequenceBilling')}</li>
          </ul>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-4)', maxWidth: 380 }}>
            <label className="text-secondary">
              {t('account.deleteConfirmPrompt')}
              <input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                disabled={busy}
                style={{ width: '100%', marginTop: 'var(--spacing-2)' }}
              />
            </label>
            <label className="text-secondary">
              {t('account.reenterPassword')}
              <PasswordInput
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={busy}
                style={{ width: '100%', marginTop: 'var(--spacing-2)' }}
              />
            </label>
            <label className="text-secondary">
              {t('account.deleteReason')}
              <select
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                disabled={busy}
                style={{ width: '100%', marginTop: 'var(--spacing-2)' }}
              >
                {REASONS.map((r) => (
                  <option key={r.value} value={r.value}>
                    {t(r.labelKey)}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {error && <p className="error-text" style={{ margin: 0 }}>{error}</p>}

          <div style={{ display: 'flex', gap: 'var(--spacing-4)' }}>
            <button
              className="btn-danger"
              disabled={busy || confirmText !== 'DELETE' || !password}
              onClick={() => void submitDelete()}
            >
              {busy ? t('common.submitting') : t('account.deletePermanently')}
            </button>
            <button
              className="btn-ghost"
              disabled={busy}
              onClick={() => {
                setStep('entry');
                setConfirmText('');
                setPassword('');
                setError(null);
              }}
            >
              {t('common.cancel')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
